import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

/**
 * The portal_* views and RPCs are not in the generated Database types yet
 * (precedent: src/lib/loginTracking.ts). Row shapes are hand-written from the
 * migration contract; swap to the generated types once they are regenerated.
 */
export const portalDb = supabase as unknown as SupabaseClient;

export interface PortalLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

/** jsonb line_items may arrive as an array or (defensively) a JSON string. */
export function parseLineItems(raw: unknown): PortalLineItem[] {
  let items: unknown = raw;
  if (typeof items === 'string') {
    try {
      items = JSON.parse(items);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(items)) return [];
  return items.map((i) => {
    const row = (i ?? {}) as Record<string, unknown>;
    return {
      description: typeof row.description === 'string' ? row.description : '',
      quantity: Number(row.quantity) || 0,
      unit_price: Number(row.unit_price) || 0,
      amount: Number(row.amount) || 0,
    };
  });
}

export interface PortalCompanyRow {
  id: string;
  slug: string;
  name: string;
  trading_name: string | null;
  legal_name: string | null;
  logo_url: string | null;
  primary_color: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  website: string | null;
  business_hours: string | null;
  street_address: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  timezone: string | null;
  abn: string | null;
  acn: string | null;
  terms_and_conditions_url: string | null;
  privacy_policy_url: string | null;
  warranty_text: string | null;
  payment_instructions: string | null;
  invoice_terms: string | null;
  tax_enabled: boolean | null;
  tax_rate: number | null;
  tax_label: string | null;
  tax_inclusive: boolean | null;
}

export interface PortalBrandingRow {
  name: string;
  trading_name: string | null;
  logo_url: string | null;
  primary_color: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
}

export interface PortalMeRow {
  link_id: string;
  company_id: string;
  customer_id: string;
  login_email: string;
  last_login_at: string | null;
  customer_number: string | null;
  customer_name: string;
  customer_type: string | null;
  company_name: string | null;
  primary_phone: string | null;
  primary_email: string | null;
  primary_address: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
}

export interface PortalJobRow {
  id: string;
  company_id: string;
  customer_id: string | null;
  lead_id: string | null;
  job_number: string;
  status: string;
  scheduled_date: string | null;
  scheduled_time_start: string | null;
  scheduled_time_end: string | null;
  job_date: string | null;
  closed_at: string | null;
  cancelled_at: string | null;
  issue_description: string | null;
  work_summary: string | null;
  customer_address: string | null;
  customer_suburb: string | null;
  customer_state: string | null;
  customer_postcode: string | null;
  service_type_name: string | null;
  tech_first_name: string | null;
  created_at: string;
}

export interface PortalEstimateRow {
  id: string;
  company_id: string;
  customer_id: string | null;
  lead_id: string | null;
  estimate_number: string;
  status: string;
  service_description: string | null;
  line_items: unknown;
  subtotal: number;
  gst_amount: number;
  estimate_amount: number;
  valid_until: string | null;
  terms: string | null;
  tax_rate_snapshot: number | null;
  tax_label_snapshot: string | null;
  tax_inclusive_snapshot: boolean | null;
  customer_name: string | null;
  customer_address: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  signed_at: string | null;
  signer_name: string | null;
  declined_at: string | null;
  created_at: string;
  can_respond: boolean;
  // Deposit on acceptance (20260924170000); pay link only while something is owed
  deposit_percent?: number | null;
  deposit_amount?: number | null;
  deposit_paid_amount?: number | null;
  deposit_payment_url?: string | null;
}

export interface PortalInvoiceRow {
  id: string;
  company_id: string;
  customer_id: string | null;
  job_id: string | null;
  invoice_number: string;
  status: string;
  issue_date: string | null;
  due_date: string | null;
  paid_date: string | null;
  line_items: unknown;
  subtotal: number;
  gst_amount: number;
  total_amount: number;
  amount_paid: number;
  amount_outstanding: number | null;
  amount_refunded: number | null;
  tax_rate_snapshot: number | null;
  tax_label_snapshot: string | null;
  tax_inclusive_snapshot: boolean | null;
  terms: string | null;
  service_address: string | null;
  customer_name: string | null;
  customer_address: string | null;
  /** Null unless the invoice is payable online (status sent/overdue). */
  payment_url: string | null;
  created_at: string;
}

/** Outstanding balance, tolerant of a null column on older rows. */
export function invoiceBalance(inv: Pick<PortalInvoiceRow, 'amount_outstanding' | 'total_amount' | 'amount_paid'>): number {
  if (inv.amount_outstanding != null) return Number(inv.amount_outstanding) || 0;
  return Math.max((Number(inv.total_amount) || 0) - (Number(inv.amount_paid) || 0), 0);
}

export function invoiceIsPayable(inv: Pick<PortalInvoiceRow, 'status' | 'amount_outstanding' | 'total_amount' | 'amount_paid'>): boolean {
  return (inv.status === 'sent' || inv.status === 'overdue') && invoiceBalance(inv) > 0;
}
